import { dateId, excelDownloadId, filterId, searchId } from "@/assets/googleTrackIds";
import {
  DepositStatementEnum,
  ListNumberQueryParamsType,
  ListQueryParamsType,
  ListStringQueryParamsType,
} from "@/pages/DigitalAccount/Saman/types";
import { useAppTranslation } from "@/utils";
import { actionFilter, wageFilter } from "@/utils/DigitalAccount";
import { OrderDirectionEnum, SamanApis } from "@payping/api";
import { PhRDownloadSimple, PhRFunnel } from "@payping/icons";
import { SDKDateObject, dateFormats, jalaliNow, toJalali, toUtc } from "@payping/utils/dateUtils";
import { countQueryParams, submitQueryParams } from "@payping/utils/query-string";
import {
  Badge,
  Button,
  DataHeader,
  Drawer,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Radio,
  Space,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import moment from "jalali-moment";
import qs from "query-string";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsyncFn } from "react-use";

interface Props {
  retry: () => void;
  loading: boolean;
}

interface FilterForm extends Omit<ListQueryParamsType, "startDate" | "endDate" | "turnOverType"> {
  dateValue?: [SDKDateObject | undefined, SDKDateObject | undefined];
  paymentType: number;
  search?: string;
}

export const AccountTurnOverFilter: FC<Props> = ({ loading, retry }) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FilterForm>();
  const [drawerForm] = LegacyForm.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const parsedSearch = qs.parse(location.search);
  const parsed = qs.parse(location.search);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { breakpoints } = usePaypingContext();
  const { md, lg } = breakpoints;
  const { startDate, endDate, referenceNumber }: ListStringQueryParamsType = qs.parse(location.search) || {};
  const { turnOverType }: ListNumberQueryParamsType =
    qs.parse(location.search, {
      parseNumbers: true,
    }) || {};
  const backFormat = dateFormats.standard.utcDateTime;

  useEffect(() => {
    form.setFieldsValue({
      dateValue: [
        toUtc({ date: startDate, dateFormat: dateFormats.standard.utcDateTime, convertTime: false }).object,
        toUtc({ date: endDate, dateFormat: dateFormats.standard.utcDateTime, convertTime: false }).object,
      ],
      paymentType: turnOverType,
    });
    drawerForm.setFieldsValue(
      lg
        ? { paymentType: turnOverType }
        : {
            paymentType: turnOverType,
            dateValue: [
              toUtc({ date: startDate, dateFormat: dateFormats.standard.utcDateTime, convertTime: false }).object,
              toUtc({ date: endDate, dateFormat: dateFormats.standard.utcDateTime, convertTime: false }).object,
            ],
          },
    );
  }, [turnOverType, form, drawerForm, startDate, backFormat, endDate, parsedSearch, referenceNumber, lg]);

  const onReset = (): void => {
    delete parsed.turnOverType;
    delete parsed.startDate;
    delete parsed.endDate;
    delete parsed.referenceNumber;
    delete parsed.search;
    navigate({ pathname: location.pathname, search: qs.stringify(parsed) });
    form.resetFields();
    drawerForm.resetFields();
    setOpenDrawer(false);
  };

  const [{ loading: loadingExcel }, downloadFile] = useAsyncFn(
    async (newWindow: Window | null) => {
      const fromDate = startDate
        ? toJalali({
            date: startDate,
            dateFormat: dateFormats.standard.utcDateTime,
            toFormat: dateFormats.ui.jalaliDate,
          }).object
        : jalaliNow({
            manipulate: [{ args: [1, "month"], method: "subtract" }],
            toFormat: dateFormats.standard.utcDateTime,
          }).object.startOf("day");

      const toDate = endDate
        ? toJalali({
            date: endDate,
            dateFormat: dateFormats.standard.utcDateTime,
            toFormat: dateFormats.ui.jalaliDate,
          }).object
        : jalaliNow({
            toFormat: dateFormats.standard.utcDateTime,
          }).object;

      // دریافت Blob از API
      const path = await SamanApis.getDepositStatementsExcelFileWithDepositStatementModelWithCancellationTokenGet(
        actionFilter(turnOverType),
        fromDate?.toDate(),
        toDate?.toDate(),
        OrderDirectionEnum.DESC,
        referenceNumber,
        wageFilter(turnOverType),
      );

      if (path.filePath !== undefined) {
        newWindow?.location?.assign?.(path.filePath);
      }
    },
    [referenceNumber, startDate, endDate, turnOverType],
  );
  return (
    <LegacyForm<FilterForm>
      form={form}
      onFinish={(values): void => {
        const date = values.dateValue || [undefined, undefined];
        const [from, to] = [date[0]?.format(backFormat), date[1]?.format(backFormat)];
        const newValues = {
          ...parsed,
          ...values,
          startDate: from || undefined,
          endDate: to || undefined,
          referenceNumber: values?.search?.length ? values.search : undefined,
        };
        submitQueryParams({
          navigate,
          newValues,
          parsedSearch,
          pathname: location.pathname,
          retry,
        });
      }}
    >
      <DataHeader
        title={<Typography.Title level={5}>{t("digitalAccount.samanTurnOver.samanAccountTurnover")}</Typography.Title>}
        filters={{
          datepickerInput: (
            <LegacyForm.Item key={1} noStyle name="dateValue">
              <LegacyDatePicker.RangePicker
                presets={[
                  {
                    label: t("common.today"),
                    value: [jalaliNow({ toFormat: dateFormats.standard.utcDateTime }).object, jalaliNow().object],
                  },
                  {
                    label: t("common.thisWeek"),
                    value: [
                      jalaliNow({ manipulate: [{ args: ["week"], method: "startOf" }] }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.lastWeek"),
                    value: [
                      jalaliNow({
                        manipulate: [
                          { args: [1, "weeks"], method: "subtract" },
                          { args: ["week"], method: "startOf" },
                        ],
                      }).object,
                      jalaliNow({
                        manipulate: [
                          { args: [1, "weeks"], method: "subtract" },
                          { args: ["week"], method: "endOf" },
                        ],
                      }).object,
                    ],
                  },
                  {
                    label: t("common.thisMonth"),
                    value: [
                      jalaliNow({ manipulate: [{ args: ["month"], method: "startOf" }] }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.lastMonth"),
                    value: [
                      jalaliNow({
                        manipulate: [{ args: [30, "days"], method: "subtract" }],
                      }).object,
                      jalaliNow().object,
                    ],
                  },
                ]}
                disabledDate={(date) =>
                  !date || date.isBefore(moment().subtract(1, "month"), "day") || date.isAfter(moment(), "day")
                }
                className={"tw:w-full"}
                onChange={() => form.submit()}
                allowClear
                data-google-track={dateId}
              />
            </LegacyForm.Item>
          ),
          searchbarInput: (
            <LegacyForm.Item name={"search"}>
              <Input.Search
                allowClear
                onSearch={() => form.submit()}
                placeholder={t("common.search")}
                addonBefore={t("digitalAccount.samanTurnOver.table.referenceNumber")}
                data-google-track={searchId}
              />
            </LegacyForm.Item>
          ),
          filtersButton: (
            <LegacyForm.Item noStyle key={2}>
              {/* < آیکن و دکمه فیلتر ها > */}
              <Badge
                className="tw:z-1 tw:w-full"
                count={countQueryParams(
                  parsedSearch,
                  lg ? { turnOverType } : { turnOverType, startDate, referenceNumber },
                )}
              >
                <Button
                  block
                  icon={<PhRFunnel width={14} height={14} />}
                  onClick={() => setOpenDrawer(true)}
                  data-google-track={filterId}
                >
                  {t("filters")}
                </Button>
              </Badge>

              <Drawer
                title={t("filter")}
                placement={md ? "left" : "bottom"}
                onClose={() => setOpenDrawer(false)}
                height={"100%"}
                width={md ? "33rem" : "100%"}
                open={openDrawer}
                extra={
                  <Space>
                    <Button
                      // eslint-disable-next-line react-hooks/immutability
                      onClick={() => {
                        onReset();
                      }}
                    >
                      {t("resetForm")}
                    </Button>
                    <Button loading={loading} type="primary" onClick={() => drawerForm.submit()}>
                      {t("applyFilter")}
                    </Button>
                  </Space>
                }
              >
                <LegacyForm
                  form={drawerForm}
                  onFinish={
                    lg
                      ? (values): void => {
                          const newValues = {
                            ...parsed,
                            turnOverType: values.paymentType,
                          };
                          submitQueryParams({
                            navigate,
                            newValues,
                            parsedSearch,
                            pathname: location.pathname,
                            retry,
                          });
                          setOpenDrawer(false);
                        }
                      : (values): void => {
                          const date = values.dateValue || [undefined, undefined];
                          const [from, to] = [date[0]?.format(backFormat), date[1]?.format(backFormat)];
                          const newValues = {
                            ...parsed,
                            ...values,
                            startDate: from || undefined,
                            endDate: to || undefined,
                            referenceNumber: values?.search?.length ? values.search : undefined,
                            turnOverType: values.paymentType,
                          };
                          submitQueryParams({
                            navigate,
                            newValues,
                            parsedSearch,
                            pathname: location.pathname,
                            retry,
                          });
                          setOpenDrawer(false);
                        }
                  }
                  layout={"vertical"}
                >
                  {!lg ? (
                    <>
                      <LegacyForm.Item label={t("date")} name="dateValue">
                        <LegacyDatePicker.RangePicker
                          presets={[
                            {
                              label: t("common.today"),
                              value: [
                                jalaliNow({ toFormat: dateFormats.standard.utcDateTime }).object,
                                jalaliNow().object,
                              ],
                            },
                            {
                              label: t("common.thisWeek"),
                              value: [
                                jalaliNow({ manipulate: [{ args: ["week"], method: "startOf" }] }).object,
                                jalaliNow().object,
                              ],
                            },
                            {
                              label: t("common.lastWeek"),
                              value: [
                                jalaliNow({
                                  manipulate: [
                                    { args: [1, "weeks"], method: "subtract" },
                                    { args: ["week"], method: "startOf" },
                                  ],
                                }).object,
                                jalaliNow({
                                  manipulate: [
                                    { args: [1, "weeks"], method: "subtract" },
                                    { args: ["week"], method: "endOf" },
                                  ],
                                }).object,
                              ],
                            },
                            {
                              label: t("common.thisMonth"),
                              value: [
                                jalaliNow({ manipulate: [{ args: ["month"], method: "startOf" }] }).object,
                                jalaliNow().object,
                              ],
                            },
                            {
                              label: t("common.lastMonth"),
                              value: [
                                jalaliNow({
                                  manipulate: [{ args: [30, "days"], method: "subtract" }],
                                }).object,
                                jalaliNow().object,
                              ],
                            },
                          ]}
                          disabledDate={(date) =>
                            !date ||
                            date.isBefore(moment().subtract(1, "month"), "day") ||
                            date.isAfter(moment(), "day")
                          }
                          className={"tw:w-full"}
                          allowClear
                          data-google-track={dateId}
                        />
                      </LegacyForm.Item>
                      <LegacyForm.Item name={"search"}>
                        <Input.Search
                          allowClear
                          onSearch={() => drawerForm.submit()}
                          placeholder={t("common.search")}
                          addonBefore={t("digitalAccount.samanTurnOver.table.referenceNumber")}
                          data-google-track={searchId}
                        />
                      </LegacyForm.Item>
                    </>
                  ) : null}
                  <LegacyForm.Item label={t("digitalAccount.samanTurnOver.turnOverType")} name="paymentType">
                    <Radio.Group defaultValue={DepositStatementEnum.ALL}>
                      <Radio.Button value={DepositStatementEnum.ALL}>{t("all")}</Radio.Button>
                      <Radio.Button value={DepositStatementEnum.DEBIT}>
                        {t("digitalAccount.yourAccount.debit")}
                      </Radio.Button>
                      <Radio.Button value={DepositStatementEnum.CREDIT}>
                        {t("digitalAccount.yourAccount.credit")}
                      </Radio.Button>
                      <Radio.Button value={DepositStatementEnum.PayPingWage}>
                        {t("digitalAccount.yourAccount.paypingWage")}
                      </Radio.Button>
                    </Radio.Group>
                  </LegacyForm.Item>
                </LegacyForm>
              </Drawer>
            </LegacyForm.Item>
          ),
          downloadButton: (
            <LegacyForm.Item key={3} noStyle>
              <Button
                block
                onClick={() => {
                  const newWindow = window.open(undefined, "_blank");
                  downloadFile(newWindow);
                }}
                icon={<PhRDownloadSimple width={14} height={14} />}
                loading={loadingExcel}
                data-google-track={excelDownloadId}
              >
                {t("downloadExcel")}
              </Button>
            </LegacyForm.Item>
          ),
        }}
      />
    </LegacyForm>
  );
};
